import { Controller } from "@hotwired/stimulus";

/**
 * Hero Slider Controller
 *
 * Auto-cycling carousel for catalog hero models with:
 * - Configurable auto-advance interval (default 6000ms)
 * - Pause on hover/focus
 * - Manual navigation via dots and arrows
 * - Keyboard navigation (arrow keys when focused)
 * - Touch swipe support for mobile
 */
export default class extends Controller<HTMLElement> {
  static targets = ["slide", "dot"];
  static values = {
    interval: { type: Number, default: 6000 },
    current: { type: Number, default: 0 },
  };

  declare readonly slideTargets: HTMLElement[];
  declare readonly dotTargets: HTMLElement[];

  declare intervalValue: number;
  declare currentValue: number;

  private autoplayTimer: ReturnType<typeof setInterval> | null = null;
  private isPaused: boolean = false;
  private touchStartX: number = 0;
  private touchEndX: number = 0;

  connect(): void {
    // Mark the initially-visible slide active so its hero video (if any)
    // starts playing. Cheap to do regardless of slide count.
    this.markActiveSlide(this.currentValue);

    if (this.slideTargets.length <= 1) return;

    // Don't call showSlide on connect - the HTML already has correct initial state
    // This prevents a flash/transition on page load
    this.setupTouchEvents();
    this.startAutoplay();
  }

  disconnect(): void {
    this.stopAutoplay();
  }

  // Navigation actions
  next(): void {
    const nextIndex = (this.currentValue + 1) % this.slideTargets.length;
    this.goToSlide(nextIndex);
  }

  prev(): void {
    const prevIndex =
      (this.currentValue - 1 + this.slideTargets.length) %
      this.slideTargets.length;
    this.goToSlide(prevIndex);
  }

  goTo(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const index = parseInt(target.dataset.index || "0", 10);
    this.goToSlide(index);
  }

  // Pause/resume on hover
  pause(): void {
    this.isPaused = true;
    this.stopAutoplay();
  }

  resume(): void {
    this.isPaused = false;
    this.startAutoplay();
  }

  // Keyboard navigation
  keydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        this.prev();
        break;
      case "ArrowRight":
        event.preventDefault();
        this.next();
        break;
    }
  }

  private goToSlide(index: number): void {
    if (index === this.currentValue) return;

    this.currentValue = index;
    this.showSlide(index);

    // Reset autoplay timer when manually navigating
    if (!this.isPaused) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  private showSlide(index: number): void {
    this.slideTargets.forEach((slide, i) => {
      if (i === index) {
        // Make visible first, then fade in
        slide.classList.remove("opacity-0", "z-0", "invisible");
        slide.classList.add("opacity-100", "z-10");
      } else {
        slide.classList.remove("opacity-100", "z-10");
        slide.classList.add("opacity-0", "z-0");
        // Add invisible after transition completes to fully hide
        setTimeout(() => {
          if (i !== this.currentValue) {
            slide.classList.add("invisible");
          }
        }, 700); // Match transition duration
      }
    });

    this.dotTargets.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add("bg-white");
        dot.classList.remove("bg-white/50");
      } else {
        dot.classList.remove("bg-white");
        dot.classList.add("bg-white/50");
      }
    });

    this.markActiveSlide(index);
  }

  // Play the active slide's hero video (if any) and pause the rest so
  // background slides don't keep decoding. Fade the video in only once
  // it actually starts playing — that way the still acts as a poster
  // until the first frame is ready, avoiding a flash of black.
  private markActiveSlide(activeIndex: number): void {
    this.slideTargets.forEach((slide, i) => {
      const isActive = i === activeIndex;
      const video = slide.querySelector<HTMLVideoElement>("[data-hero-video]");
      if (!video) return;
      if (isActive) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise
            .then(() => video.classList.remove("opacity-0"))
            .catch(() => undefined);
        } else {
          video.classList.remove("opacity-0");
        }
      } else {
        video.pause();
        video.currentTime = 0;
        video.classList.add("opacity-0");
      }
    });
  }

  private startAutoplay(): void {
    if (this.autoplayTimer || this.isPaused) return;

    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.intervalValue);
  }

  private stopAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  private setupTouchEvents(): void {
    this.element.addEventListener(
      "touchstart",
      (e: TouchEvent) => {
        this.touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.element.addEventListener(
      "touchend",
      (e: TouchEvent) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      },
      { passive: true }
    );
  }

  private handleSwipe(): void {
    const diff = this.touchStartX - this.touchEndX;
    const threshold = 50;

    if (Math.abs(diff) < threshold) return;

    if (diff > 0) {
      this.next(); // Swipe left = next
    } else {
      this.prev(); // Swipe right = prev
    }
  }
}
